import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { calculateAmericanPrice } from '~shared/components/sport-event/utils';
import {
    useSsSelector as useSelector,
} from '~SpectateStore';
import {
    selectIsSelectionInBetslip,
} from '~Betslip/selectors/userSelectionsSelectors';
import SwitchableOdds from '../SwitchableOdds';
import { toggleSelectionToBetslip } from './utils/toggleSelectionToBetslip';
import './betbutton.scss';

const BetButton = ({
    selection_id,
    label = null,
    sp_only,
    active,
    tradable,
    betable,
    decimal_price,
    fraction_price,
    american_price,
    sport_slug,
    highlightPriceUpdate = true,
    gtmAttribute,
    isScorecast = false,
    isMulti = false,
    limitOddsDisplay = false,
}) => {
    const selectionInBetslip = useSelector(state => selectIsSelectionInBetslip(state, selection_id, isMulti));
    const spectateProduct = `${gtmAttribute}${selectionInBetslip ? 'remove-from-bet-slip' : 'add-to-bet-slip'}`;
    const [previousPrice, setPreviousPrice] = useState(decimal_price);
    const [americanPrice, setAmericanPrice] = useState(american_price ?? calculateAmericanPrice(decimal_price));
    const [priceAnimationState, setPriceAnimationState] = useState('');
    const dataTestId = `selection;${selectionInBetslip ? 'selected' : 'add-to-bet-slip'}`;

    // needed for switchable odds component
    const selection = {
        sport_slug,
        sp_only,
        active,
        tradable,
        decimal_price: decimal_price ? String(decimal_price) : null,
        fraction_price,
        american_price: americanPrice,
    };

    const suspended = !selection_id || !betable;
    const isDeactivated = suspended && !selectionInBetslip;

    useEffect(() => {
        const decimalPrice = Number(decimal_price);

        if (highlightPriceUpdate && !isDeactivated) {
            if (decimalPrice < previousPrice) {
                setPriceAnimationState('shortened');
                setTimeout(() => setPriceAnimationState(''), 850);
            } else if (decimalPrice > previousPrice) {
                setPriceAnimationState('lengthened');
                setTimeout(() => setPriceAnimationState(''), 850);
            }
        }

        // american price does not come through WS
        // some components do not calculate american price on update
        setAmericanPrice(calculateAmericanPrice(decimalPrice));
        setPreviousPrice(decimalPrice);
    }, [decimal_price]);

    const betButtonClasses = classNames('bet-button-new', {
        'bet-button-new--selected': selectionInBetslip,
        [`bet-button-new--price-${priceAnimationState}`]: priceAnimationState !== '',
        'bet-button-new--deactivated': isDeactivated,
        'bet-button-new--suspended': suspended,
    });

    return (
        <div
            className={betButtonClasses}
            key={`bet-button-${selection_id}`}
            onClick={(e) => {
                const betLocate = e.target.closest('[data-ga_component]')
                ?.getAttribute('data-ga_component')
                || 'unknown_component';

                if (!isDeactivated) {
                    toggleSelectionToBetslip(selection_id, selectionInBetslip, betLocate, isScorecast, isMulti);
                }
            }}
            data-spectate-product={gtmAttribute ? spectateProduct : null}
            data-test-id={dataTestId}
            data-selection-id={selection_id}
            {...label && { 'data-label': label }}
            >
            {
                suspended
                ? <div className="bet-button-new__suspended-icon"></div>
                : <SwitchableOdds
                    selection={selection}
                    limitOddsDisplay={limitOddsDisplay}
                />
            }
        </div>
    );
};

BetButton.propTypes = {
    selection_id: PropTypes.string,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    sp_only: PropTypes.bool,
    betable: PropTypes.bool,
    active: PropTypes.bool,
    tradable: PropTypes.bool,
    decimal_price: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    fraction_price: PropTypes.string,
    american_price: PropTypes.string,
    sport_slug : PropTypes.string,
    highlightPriceUpdate: PropTypes.bool,
    gtmAttribute: PropTypes.string,
    isScorecast: PropTypes.bool,
    isMulti: PropTypes.bool,
    limitOddsDisplay: PropTypes.bool,
};

export default BetButton;
